import type { HealthRFPParameters } from "@/components/rfp/HealthRFPForm";
import type { DentalRFPParameters } from "@/components/rfp/DentalRFPForm";
import type { VisionRFPParameters } from "@/components/rfp/VisionRFPForm";
import type { LifeRFPParameters } from "@/components/rfp/LifeRFPForm";
import type { DisabilityRFPParameters } from "@/components/rfp/DisabilityRFPForm";
import type { VoluntaryRFPParameters } from "@/components/rfp/VoluntaryRFPForm";

export type ProposalTemplate = "modern" | "classic" | "minimal" | "corporate";
export type ColorScheme = "blue" | "green" | "purple" | "orange" | "slate";

export interface ProposalSettings {
  template: ProposalTemplate;
  colorScheme: ColorScheme;
  showLogo: boolean;
  showBenefitsDetail: boolean;
  showEmployeeBreakdown: boolean;
  coverLetter: string;
}

/**
 * Employer contribution strategy for a quote. `employee` applies to the
 * employee's own premium; `dependent` applies to the dependents' premium
 * (a percentage of the dependent premium, or a flat dollar amount toward it).
 */
export interface QuoteContributionConfig {
  type: "percentage" | "fixed";
  employee: number;
  dependent: number;
}

export const DEFAULT_QUOTE_CONTRIBUTION: QuoteContributionConfig = {
  type: "percentage",
  employee: 70,
  dependent: 0,
};

/** Accepts the legacy shape ({ employeeOnly, employeeWithDependents }) too. */
export function normalizeQuoteContribution(raw: any): QuoteContributionConfig {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_QUOTE_CONTRIBUTION };
  const type = raw.type === "fixed" ? "fixed" : "percentage";
  const num = (v: any, fallback: number) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const employee = num(raw.employee ?? raw.employeeOnly, DEFAULT_QUOTE_CONTRIBUTION.employee);
  const dependent = num(raw.dependent, DEFAULT_QUOTE_CONTRIBUTION.dependent);
  return { type, employee, dependent };
}

export interface QuoteData {
  clientType: "client" | "prospect" | null;
  selectedEntity: any;
  effectiveDate: Date | null;
  coverageTypes: string[];
  selectedEmployees: number[];
  selectedDependents: number[];
  selectedCarriers: Record<string, number[]>;
  // RFP only. "ga" sends the whole RFP to one general agent instead of to each
  // carrier's own contact. All or nothing per RFP — no mixing, by decision.
  // selectedCarriers still drives everything downstream either way: it becomes
  // the list of carriers asked for in the email body, and it is what proposals
  // are matched back to on the way in.
  rfpSendVia?: "carriers" | "ga";
  // An agency generally works with one or two GAs and may want the RFP to go to
  // both, so this is a list, not a single pick. These are general_agencies ids
  // — the firms — and every sales contact at a picked firm gets the email.
  rfpGeneralAgencyIds?: number[];
  /**
   * Which carriers to ask of which GA, as "generalAgencyId:carrierId" pairs.
   *
   * A GA only holds appointments with some carriers, and the broker picks from
   * that roster per firm, so this is a choice rather than something derivable.
   * The same carrier asked of two GAs appears twice on purpose: two quotes to
   * choose between is a reason to use two GAs.
   *
   * selectedCarriers above stays the flat roll-up of these, because step 5 and
   * the RFP detail screens know nothing about GAs.
   */
  rfpGeneralAgencyAsks?: string[];
  /** Date-only ("2026-09-30"). Absent means two weeks from the send. */
  rfpRespondBy?: string | null;
  /**
   * The broker's own prose, replacing the generated wording. One set for the
   * whole RFP. rfpEmailGreeting is the salutation word ("Dear"), never a name.
   */
  rfpEmailGreeting?: string | null;
  rfpEmailIntro?: string | null;
  rfpEmailClosing?: string | null;
  rfpEmailSignature?: string | null;
  selectedPlans: Record<string, any[]>;
  /**
   * The group's current plan per coverage type — one plan, not a list. This
   * is a fact about what they already have, not an alternative being
   * shopped like selectedPlans. Picked from the same search/filter UI as
   * selectedPlans in Step 5; a new pick replaces rather than appends.
   */
  currentPlans: Record<string, any | null>;
  /** Per coverage type: show the current-vs-renewal comparison in Step 5. */
  compareCurrentEnabled: Record<string, boolean>;
  confirmedRatingArea?: {
    id: number;
    label: string;
  } | null;
  rfpParameters: {
    health?: HealthRFPParameters;
    dental?: DentalRFPParameters;
    vision?: VisionRFPParameters;
    life?: LifeRFPParameters;
    disability?: DisabilityRFPParameters;
    voluntary?: VoluntaryRFPParameters;
  };
  proposalSettings?: ProposalSettings;
  contribution?: QuoteContributionConfig;
}

export interface StepDefinition {
  id: number;
  title: string;
  description: string;
}

export interface StepProps {
  quoteData: QuoteData;
  updateQuoteData: (updates: Partial<QuoteData>) => void;
}
